import React, { useState, useRef, useEffect } from 'react';
import { GlobeIcon, CaretDownIcon, CheckIcon } from '@phosphor-icons/react';
import { QwenTTSLanguage } from '../../types';
import { SUPPORTED_LANGUAGES } from '../../lib/characterVoiceService';

interface LanguageSelectorProps {
  value: QwenTTSLanguage;
  onChange: (language: QwenTTSLanguage) => void;
  className?: string;
  disabled?: boolean;
  compact?: boolean;
}

// Language flags/emojis for UI enhancement
export const LANGUAGE_FLAGS: Record<string, string> = {
  'Auto': '🌐',
  'Chinese': '🇨🇳',
  'English': '🇺🇸',
  'Japanese': '🇯🇵',
  'Korean': '🇰🇷',
  'German': '🇩🇪',
  'French': '🇫🇷',
  'Russian': '🇷🇺',
  'Portuguese': '🇵🇹',
  'Spanish': '🇪🇸',
  'Italian': '🇮🇹',
};

export const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  value,
  onChange,
  className = '',
  disabled = false,
  compact = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const selectedLanguage = SUPPORTED_LANGUAGES.find(l => l.id === value) || SUPPORTED_LANGUAGES[0];
  const flag = LANGUAGE_FLAGS[value] || '🌐';

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSelect = (langId: QwenTTSLanguage) => {
    onChange(langId);
    setIsOpen(false);
  };

  if (compact) {
    return (
      <div className={className} ref={dropdownRef}>
        <div className="relative">
          <button
            onClick={() => !disabled && setIsOpen(!isOpen)}
            disabled={disabled}
            className={`flex items-center gap-1 px-1.5 py-1 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer ${
              isOpen ? 'bg-graphite text-dust' : 'text-dust/40 hover:text-dust/70 hover:bg-graphite/50'
            }`}
            title={selectedLanguage.name}
          >
            {value === 'Auto' ? (
              <GlobeIcon className="w-4 h-4" />
            ) : (
              <span className="text-sm leading-none">{flag}</span>
            )}
            <CaretDownIcon className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          </button>

          {/* Dropdown Menu - opens upward */}
          {isOpen && (
            <div className="absolute bottom-full right-0 mb-1 py-1 bg-carbon border border-graphite rounded-lg shadow-xl z-[100] min-w-[140px] max-h-[200px] overflow-y-auto">
            {SUPPORTED_LANGUAGES.map((lang) => {
              const langFlag = LANGUAGE_FLAGS[lang.id] || '🌐';
              const isSelected = value === lang.id;
              return (
                <button
                  key={lang.id}
                  onClick={() => handleSelect(lang.id)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-xs text-left transition-colors ${
                    isSelected
                      ? 'bg-slate-blue/20 text-slate-blue-soft'
                      : 'text-dust hover:bg-graphite'
                  }`}
                >
                  <span className="text-sm">{langFlag}</span>
                  <span className="flex-1">{lang.name}</span>
                  {isSelected && <CheckIcon className="w-3.5 h-3.5" weight="bold" />}
                </button>
              );
            })}
          </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <label className="block text-xs text-dust/60 mb-2">Language</label>
      <button
        onClick={() => !disabled && setIsOpen(!isOpen)}
        disabled={disabled}
        className="w-full flex items-center gap-3 bg-onyx border border-graphite rounded-xl px-4 py-3 text-sm text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed hover:border-iron focus:outline-none focus:border-slate-blue/50"
      >
        <GlobeIcon className="w-4 h-4 text-iron" />
        <span className="text-sm">{flag}</span>
        <span className="flex-1 text-left">{selectedLanguage.name}</span>
        <CaretDownIcon className={`w-4 h-4 text-iron transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 mt-1 py-1 bg-carbon border border-graphite rounded-xl shadow-xl z-50 max-h-[300px] overflow-y-auto">
          {SUPPORTED_LANGUAGES.map((lang) => {
            const langFlag = LANGUAGE_FLAGS[lang.id] || '🌐';
            const isSelected = value === lang.id;
            return (
              <button
                key={lang.id}
                onClick={() => handleSelect(lang.id)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-left transition-colors ${
                  isSelected
                    ? 'bg-slate-blue/20 text-slate-blue-soft'
                    : 'text-dust hover:bg-graphite'
                }`}
              >
                <span>{langFlag}</span>
                <span className="flex-1">{lang.name}</span>
                {isSelected && <CheckIcon className="w-4 h-4" weight="bold" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

// Compact version for inline use
export const LanguageBadge: React.FC<{ language: QwenTTSLanguage; className?: string }> = ({
  language,
  className = '',
}) => {
  const langInfo = SUPPORTED_LANGUAGES.find(l => l.id === language);
  const flag = LANGUAGE_FLAGS[language] || '🌐';

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-graphite text-xs text-dust/80 ${className}`}>
      <span>{flag}</span>
      <span>{langInfo?.name || language}</span>
    </span>
  );
};

export default LanguageSelector;
